import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import PageLoader from "./PageLoader";

const getVisibleKeys = (row) =>
  Object.keys(row).filter(
    (key) => key !== "id" && key !== "item_id" && key !== "is_internal",
  );

const formatHeader = (key) => {
  if (key === "customer_phone") return "Phone";
  if (key === "customer_name") return "Name";
  if (key === "customer_email") return "Email";
  return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const isNumericDisplay = (value) => {
  if (typeof value === "number") return true;
  if (typeof value !== "string") return false;

  const cleaned = value.replace(/[£,\s]/g, "");
  return cleaned !== "" && !Number.isNaN(Number(cleaned));
};

const AnalyticsModals = ({
  modalData,
  modalTitle,
  detailsModal,
  onCloseMain,
  onCloseDetails,
}) => {
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    setExpandedOrderId(null);
  }, [detailsModal.open, detailsModal.title, detailsModal.type]);

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const getOrderTypeClasses = (orderType) => {
    const type = String(orderType || "")
      .trim()
      .toLowerCase();

    if (type === "delivery") return "bg-blue-100 text-blue-700";
    if (type === "pickup") return "bg-amber-100 text-amber-700";
    if (type === "eat-in" || type === "dine-in") {
      return "bg-emerald-100 text-emerald-700";
    }

    return "bg-slate-100 text-slate-700";
  };

  const renderOrderItems = (items = []) => {
    if (!Array.isArray(items) || !items.length) {
      return (
        <p className="text-sm italic text-slate-500">No items available.</p>
      );
    }

    return (
      <div className="space-y-3">
        {items.map((item) => {
          const isRemoved =
            Number(item?.is_removed) === 1 || item?.is_removed === true;

          return (
            <div
              key={item.id}
              className={`relative rounded-lg border p-3 ${
                isRemoved
                  ? "border-slate-300 bg-slate-100/90 opacity-60"
                  : "border-slate-200"
              }`}
            >
              {isRemoved && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg">
                    <i className="fa-solid fa-ban text-lg"></i>
                  </div>
                </div>
              )}

              <div className="flex justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-bold text-base ${
                      isRemoved
                        ? "text-slate-400 line-through"
                        : "text-slate-900"
                    }`}
                  >
                    {item.name} x {item.quantity}
                  </p>

                  {item.required_option_name && (
                    <p
                      className={`mt-1 text-sm ${
                        isRemoved
                          ? "text-slate-400 line-through"
                          : "text-slate-600"
                      }`}
                    >
                      {item.required_option_name}
                      {parseFloat(item.required_option_price || 0) > 0
                        ? ` (£${parseFloat(item.required_option_price).toFixed(2)})`
                        : ""}
                    </p>
                  )}

                  {(item.selectedOptionalOptions || []).length > 0 && (
                    <div className="mt-2">
                      <p
                        className={`text-xs font-bold uppercase tracking-wider ${
                          isRemoved ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        Optional:
                      </p>
                      <ul
                        className={`mt-1 space-y-1 text-sm ${
                          isRemoved ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.selectedOptionalOptions.map((opt, idx) => (
                          <li
                            key={idx}
                            className={isRemoved ? "line-through" : ""}
                          >
                            {opt.name} x {opt.quantity} (£
                            {parseFloat(opt.price || 0).toFixed(2)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(item.swallowOptions || []).length > 0 && (
                    <div className="mt-2">
                      <p
                        className={`text-xs font-bold uppercase tracking-wider ${
                          isRemoved ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        Swallow:
                      </p>
                      <ul
                        className={`mt-1 space-y-1 text-sm ${
                          isRemoved ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.swallowOptions.map((sw, idx) => (
                          <li
                            key={idx}
                            className={isRemoved ? "line-through" : ""}
                          >
                            {sw.name} x {sw.quantity} (£
                            {parseFloat(sw.price || 0).toFixed(2)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(item.proteins || []).length > 0 && (
                    <div className="mt-2">
                      <p
                        className={`text-xs font-bold uppercase tracking-wider ${
                          isRemoved ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        Protein(s):
                      </p>
                      <ul
                        className={`mt-1 space-y-1 text-sm ${
                          isRemoved ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.proteins.map((p, idx) => (
                          <li
                            key={idx}
                            className={isRemoved ? "line-through" : ""}
                          >
                            {p.name} x {p.quantity} (£
                            {parseFloat(p.price || 0).toFixed(2)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div
                  className={`text-right font-bold text-sm shrink-0 ${
                    isRemoved ? "text-slate-400 line-through" : "text-slate-900"
                  }`}
                >
                  £{parseFloat(item.total_price || 0).toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderOrdersByDayCards = () => {
    const dayTotal = detailsModal.rows.reduce(
      (sum, order) => sum + parseFloat(order.total_amount || order.total || 0),
      0,
    );

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-sm text-slate-600">
            Total Orders:{" "}
            <span className="font-semibold">{detailsModal.rows.length}</span>
          </p>

          <p className="text-sm text-slate-600">
            Total:{" "}
            <span className="font-semibold text-slate-900">
              £{dayTotal.toFixed(2)}
            </span>
          </p>
        </div>

        <div className="max-h-[70vh] space-y-3 overflow-auto sidebar-scrollbar">
          {detailsModal.rows.map((order) => {
            const isDelivery =
              String(order.order_type || "")
                .trim()
                .toLowerCase() === "delivery";

            return (
              <div
                key={order.id}
                className="cursor-pointer rounded-lg border p-4 hover:bg-slate-50 transition"
                onClick={() => toggleOrder(order.id)}
              >
                <div className="flex justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span>{order.order_date || "—"}</span>
                      <span>{order.order_time || "—"}</span>
                      {order.order_number && (
                        <span className="font-medium">
                          #{order.order_number}
                        </span>
                      )}
                    </div>

                    <p className="font-semibold text-slate-900">
                      Ref: {order.id}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <p className="text-slate-600">
                        {order.customer_name || "Guest / Walk-in"}
                      </p>

                      {order.customer_phone && (
                        <p className="text-slate-500 tabular-nums">{order.customer_phone}</p>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-semibold text-slate-900">
                      £
                      {parseFloat(
                        order.total_amount || order.total || 0,
                      ).toFixed(2)}
                    </p>

                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${getOrderTypeClasses(order.order_type)}`}
                    >
                      {order.order_type || "—"}
                    </span>
                  </div>
                </div>

                {expandedOrderId === order.id && (
                  <div
                    className="mt-4 space-y-4 text-sm border-t border-slate-100 pt-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isDelivery && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="font-semibold text-slate-800">
                          Delivery Details
                        </p>

                        {order.delivery_address && (
                          <p className="mt-2 text-slate-700 text-sm">
                            <strong>Address:</strong>{" "}
                            {typeof order.delivery_address === "object"
                              ? [
                                  order.delivery_address.line1,
                                  order.delivery_address.line2,
                                  order.delivery_address.city,
                                  order.delivery_address.postcode,
                                ]
                                  .filter(Boolean)
                                  .join(", ")
                              : order.delivery_address}
                          </p>
                        )}

                        {order.postcode && (
                          <p className="text-slate-700">
                            <strong>Postcode:</strong> {order.postcode}
                          </p>
                        )}
                      </div>
                    )}

                    {renderOrderItems(order.items)}

                    {order.notes && (
                      <p className="rounded-lg bg-amber-50 p-3 text-slate-700 italic border border-amber-100">
                        <strong>Notes:</strong> {order.notes}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDetailsModalContent = () => {
    if (detailsModal.loading) {
      return <PageLoader message="Fetching Details..." />;
    }

    if (detailsModal.message) {
      return (
        <div className="py-8 text-center text-sm text-red-500">
          {detailsModal.message}
        </div>
      );
    }

    if (!detailsModal.rows.length) {
      return (
        <div className="py-8 text-center text-sm text-slate-400">
          No data available
        </div>
      );
    }

    if (detailsModal.type === "ordersByDay") {
      return renderOrdersByDayCards();
    }

    if (detailsModal.type === "itemBreakdown") {
      const totalQuantity = detailsModal.rows.reduce(
        (sum, row) => sum + Number(row.quantity_sold || 0),
        0,
      );

      const totalRevenue = detailsModal.rows.reduce(
        (sum, row) => sum + Number(row.total_revenue || 0),
        0,
      );

      return (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
                <th className="w-[40%] py-3 pr-3 font-semibold break-words">
                  Date
                </th>
                <th className="w-[28%] py-3 px-2 text-center font-semibold break-words">
                  Quantity Sold
                </th>
                <th className="w-[32%] py-3 px-2 text-center font-semibold break-words">
                  Total Revenue (£)
                </th>
              </tr>
            </thead>

            <tbody>
              {detailsModal.rows.map((row, idx) => (
                <tr
                  key={idx}
                  className="border-b border-slate-50 last:border-b-0"
                >
                  <td className="py-3 pr-3 text-slate-700">{row.period}</td>
                  <td className="px-2 py-3 text-center tabular-nums text-slate-700">
                    {row.quantity_sold}
                  </td>
                  <td className="px-2 py-3 text-center tabular-nums font-semibold text-slate-700">
                    £{Number(row.total_revenue || 0).toFixed(2)}
                  </td>
                </tr>
              ))}

              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                <td className="py-3 pr-3 text-slate-900">Total</td>
                <td className="px-2 py-3 text-center tabular-nums text-slate-900">
                  {totalQuantity}
                </td>
                <td className="px-2 py-3 text-center tabular-nums text-slate-900">
                  £{totalRevenue.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {modalData && (
        <Modal
          title={modalTitle}
          onClose={onCloseMain}
          showCancel={false}
          showSubmitButton={false}
          className="max-w-5xl"
        >
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
                  {modalData[0] &&
                    getVisibleKeys(modalData[0]).map((key, index) => (
                      <th
                        key={key}
                        className={`py-3 font-semibold break-words ${
                          index === 0 ? "pr-3 text-left w-[40%]" : "px-2 text-center"
                        }`}
                      >
                        {formatHeader(key)}
                      </th>
                    ))}
                </tr>
              </thead>

              <tbody>
                {modalData.map((row, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition"
                  >
                    {Object.entries(row)
                      .filter(
                        ([key]) =>
                          key !== "id" &&
                          key !== "item_id" &&
                          key !== "is_internal",
                      )
                      .map(([key, val], cellIndex) => {
                        let displayValue = val;

                        if (
                          typeof val === "number" &&
                          (key.includes("revenue") || key === "total_value")
                        ) {
                          displayValue = val.toFixed(2);
                        }

                        const isNumeric = isNumericDisplay(
                          String(displayValue ?? ""),
                        );

                        return (
                          <td
                            key={key}
                            className={`py-3 ${
                              cellIndex === 0
                                ? "pr-3 text-left font-medium text-slate-900"
                                : "px-2 text-center tabular-nums text-slate-700"
                            } ${isNumeric && cellIndex !== 0 ? "font-bold" : ""}`}
                          >
                            {displayValue || "—"}
                          </td>
                        );
                      })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {detailsModal.open && (
        <Modal
          title={detailsModal.title}
          onClose={onCloseDetails}
          showCancel={false}
          showSubmitButton={false}
          className="max-w-5xl"
        >
          {renderDetailsModalContent()}
        </Modal>
      )}
    </>
  );
};

export default AnalyticsModals;
